import React, { useState, useRef, useEffect } from 'react';
import {
  EllipsisVerticalIcon,
  DocumentArrowDownIcon,
  PencilSquareIcon,
  CurrencyRupeeIcon,
} from '@heroicons/react/24/solid';

const VehicleActionsMenu = ({ vehicle, onEdit, onDownload, onViewExpensesClick }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* ✅ Desktop view: Edit + 3-dot dropdown */}
      <div className="hidden md:flex items-center gap-2 relative" ref={menuRef}>
        <button
          onClick={onEdit}
          className="p-1 rounded-full hover:bg-gray-100"
          title="Edit"
        >
          <PencilSquareIcon className="h-5 w-5 text-blue-600" />
        </button>
        <button
          onClick={() => onViewExpensesClick(vehicle)}
          className="p-1 rounded-full hover:bg-gray-100"
          title="View Expenses"
        >
          <CurrencyRupeeIcon className="h-5 w-5 text-green-700" />
        </button>

        <div className="relative">
          <button
            onClick={() => setOpen(!open)}
            className="p-1 rounded-full hover:bg-gray-100"
            title="More"
          >
            <EllipsisVerticalIcon className="h-5 w-5 text-gray-700" />
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded shadow-lg z-50">
              { (vehicle.rc_doc && vehicle.rc_doc != null) && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onDownload('rc');
                  }}
                  className="flex items-center px-4 py-2 w-full text-left text-sm hover:bg-gray-100"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2 text-blue-600" />
                  RC Document
                </button>
              )}
              { (vehicle.insurance_doc && vehicle.insurance_doc != null) && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onDownload('insurance');
                  }}
                  className="flex items-center px-4 py-2 w-full text-left text-sm hover:bg-gray-100"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2 text-green-600" />
                  Insurance
                </button>
              )}
              { (vehicle.fc_doc && vehicle.fc_doc != null) && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onDownload('fc');
                  }}
                  className="flex items-center px-4 py-2 w-full text-left text-sm hover:bg-gray-100"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2 text-yellow-600" />
                  FC Document
                </button>
              )}
              { (vehicle.national_permit_doc && vehicle.national_permit_doc != null) && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onDownload('national_permit');
                  }}
                  className="flex items-center px-4 py-2 w-full text-left text-sm hover:bg-gray-100"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2 text-purple-600" />
                  Nation Permit
                </button>
              )}
              { (vehicle.state_permit_doc && vehicle.state_permit_doc != null) && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onDownload('state_permit');
                  }}
                  className="flex items-center px-4 py-2 w-full text-left text-sm hover:bg-gray-100"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2 text-pink-600" />
                  State Permit
                </button>
              )}
              { (vehicle.pollution_cert_doc && vehicle.pollution_cert_doc != null) && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onDownload('pollution_cert');
                  }}
                  className="flex items-center px-4 py-2 w-full text-left text-sm hover:bg-gray-100"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2 text-red-600" />
                  Pollution
                </button>
              )}
              { (vehicle.tax_receipt_doc && vehicle.tax_receipt_doc != null) && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onDownload('tax_receipt');
                  }}
                  className="flex items-center px-4 py-2 w-full text-left text-sm hover:bg-gray-100"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2 text-teal-600" />
                  Tax Receipt
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      
      {/* ✅ Mobile View: Show all actions in 2 columns with different colors */}
      <div className="md:hidden flex flex-col gap-2 mt-2">
          <button
            onClick={onEdit}
            className="flex items-center text-blue-600"
          >
            <PencilSquareIcon className="w-4 h-4 mr-1" />
            Edit
          </button>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onDownload('rc')}
              className="flex items-center text-green-600"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
              RC
            </button>
            <button
              onClick={() => onDownload('insurance')}
              className="flex items-center text-yellow-600"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
              Insurance
            </button>
            <button
              onClick={() => onDownload('fc')}
              className="flex items-center text-purple-600"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
              FC
            </button>
            <button
              onClick={() => onDownload('national_permit')}
              className="flex items-center text-pink-600"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
              National
            </button>
            <button
              onClick={() => onDownload('state_permit')}
              className="flex items-center text-red-600"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
              State
            </button>
            <button
              onClick={() => onDownload('pollution_cert')}
              className="flex items-center text-teal-600"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
              Pollution
            </button>
            <button
              onClick={() => onDownload('tax_receipt')}
              className="flex items-center text-indigo-600"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
              Tax
            </button>
          </div>
      </div>
    </>
  );
};

export default VehicleActionsMenu;
